import { useMemo, useState } from "react";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { Topbar } from "../../components/AppShell";
import { LoadingPanel } from "../../components/LoadingPanel";
import { FiltersBar, applyNonTimeFilters } from "../../lib/funnel/filters";
import type { Filters } from "../../lib/funnel/filters";
import type { FilterPillSpec } from "../../components/FilterPills";
import { EmptyState } from "../../lib/funnel/charts";
import { groupCountry, fmtDate } from "../../lib/funnel/helpers";
import {
  userDimsSQL, activitySQL, mapUserDimRows, mapActivityRows,
} from "./sql";
import type { UserDimRow, ActivityRow } from "./sql";
import {
  recentWeekStarts, joinActivity, weeklySeries,
  seatPctActiveForWeek, distinctEntities, activityInWeek,
} from "./helpers";
import type { Entity } from "./helpers";
import { FEATURE_ORDER } from "./definitions";
import type { FeatureKey } from "./definitions";
import { KpiTiles } from "./panels/KpiTiles";
import { WauTrend } from "./panels/WauTrend";
import { FeatureBreakdown } from "./panels/FeatureBreakdown";
import type { FeatureRow } from "./panels/FeatureBreakdown";
import { DefinitionsPanel } from "./panels/DefinitionsPanel";

export function MarlooWau() {
  const [filters, setFilters] = useState<Filters>(() => ({
    months: [],
    countries: [],
    roles: [],
    companySizes: [],
    excludeSupport: false,
    granularity: "month",
    cohortDayFrom: null,
    cohortDayTo: null,
    demoBucketing: "first_demo_date",
  }));
  const [entity, setEntity] = useState<Entity>("user");
  const weekStarts = useMemo(() => recentWeekStarts(), []);
  const [selectedWeek, setSelectedWeek] = useState<string>(() => weekStarts[weekStarts.length - 1] ?? "");

  const dimsResult = useAdminQuery(userDimsSQL);
  const activityResult = useAdminQuery(activitySQL);

  const dimRows = useMemo<UserDimRow[]>(() => {
    if (!dimsResult.data) return [];
    return mapUserDimRows(dimsResult.data.columns, dimsResult.data.rows as unknown[][]);
  }, [dimsResult.data]);
  const activityRows = useMemo<ActivityRow[]>(() => {
    if (!activityResult.data) return [];
    return mapActivityRows(activityResult.data.columns, activityResult.data.rows as unknown[][]);
  }, [activityResult.data]);

  const availableCountries = useMemo(() => {
    const set = new Set<string>();
    dimRows.forEach((u) => set.add(groupCountry(u.country_code)));
    return Array.from(set);
  }, [dimRows]);
  const availableRoles = useMemo(() => {
    const set = new Set<string>();
    dimRows.forEach((u) => set.add(u.user_role ?? "unknown"));
    return Array.from(set).sort();
  }, [dimRows]);

  const filteredDims = useMemo(() => applyNonTimeFilters(dimRows, filters), [dimRows, filters]);

  const joinedActivity = useMemo(
    () => joinActivity(filteredDims, activityRows),
    [filteredDims, activityRows],
  );

  const distinctUsersInView = useMemo(() => {
    const set = new Set<string>();
    filteredDims.forEach((u) => set.add(u.user_external_id));
    return set.size;
  }, [filteredDims]);

  const trendData = useMemo(() => {
    return weeklySeries(joinedActivity, entity, "overall", weekStarts)
      .map((p) => ({ label: p.week_start, value: p.value }));
  }, [joinedActivity, entity, weekStarts]);

  const isCurrentWeek = selectedWeek === weekStarts[weekStarts.length - 1];
  const selectedIdx = weekStarts.indexOf(selectedWeek);
  const priorWeekStart = selectedIdx >= 4 ? weekStarts[selectedIdx - 4]! : "";

  const currentKpiActivity = useMemo(
    () => activityInWeek(joinedActivity, selectedWeek),
    [joinedActivity, selectedWeek],
  );

  const priorKpiActivity = useMemo(
    () => activityInWeek(joinedActivity, priorWeekStart),
    [joinedActivity, priorWeekStart],
  );

  const currentCount = useMemo(
    () => distinctEntities(currentKpiActivity, entity),
    [currentKpiActivity, entity],
  );
  const priorCount = useMemo(
    () => distinctEntities(priorKpiActivity, entity),
    [priorKpiActivity, entity],
  );

  const seatPct = useMemo(() => {
    if (entity !== "company") return null;
    return seatPctActiveForWeek(filteredDims, currentKpiActivity).pct;
  }, [entity, filteredDims, currentKpiActivity]);

  const overallMomPct = useMemo(
    () => priorCount === 0 ? null : ((currentCount - priorCount) / priorCount) * 100,
    [currentCount, priorCount],
  );

  const featureRows = useMemo<FeatureRow[]>(() => {
    return FEATURE_ORDER.map((f: FeatureKey) => {
      const featureCurrent = currentKpiActivity.filter((a) => a.feature === f);
      const featurePrior = priorKpiActivity.filter((a) => a.feature === f);
      const value = distinctEntities(featureCurrent, entity);
      const priorValue = distinctEntities(featurePrior, entity);
      const trend = weeklySeries(joinedActivity, entity, f, weekStarts)
        .map((p) => ({ label: p.week_start, value: p.value }));
      const momPct = priorValue === 0 ? null : ((value - priorValue) / priorValue) * 100;
      const momDelta = momPct === null || overallMomPct === null ? null : momPct - overallMomPct;
      return { feature: f, value, trend, momPct, momDelta };
    });
  }, [joinedActivity, currentKpiActivity, priorKpiActivity, entity, weekStarts, overallMomPct]);

  const pagePills = useMemo<FilterPillSpec[]>(() => {
    const pills: FilterPillSpec[] = [];
    pills.push({ kind: "cohort", label: `Week of ${fmtDate(selectedWeek)}` });
    if (filters.excludeSupport) pills.push({ kind: "support", label: "Excludes support" });
    pills.push({ kind: "mode", label: entity === "user" ? "Per-user WAU" : "Per-company WAU" });
    return pills;
  }, [filters, entity, selectedWeek]);

  const windowLabel = isCurrentWeek ? "this week" : `week of ${fmtDate(selectedWeek)}`;
  const isLoading = dimsResult.isLoading || activityResult.isLoading;
  const isError = dimsResult.isError || activityResult.isError;
  const error = (dimsResult.error ?? activityResult.error) as Error | null;

  return (
    <>
      <Topbar crumbs={["Dashboards", "Weekly Active Users"]} />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Growth, engagement</div>
            <h1>Weekly Active Users</h1>
            <p className="lede">
              How many users (or companies) are active each week across Marloo's core features:
              meetings, Client View, documents, tasks, and forms. Weeks run Monday to Sunday.
              Toggle between per-user and per-company entities.
            </p>
          </div>
        </div>

        <FiltersBar
          filters={filters}
          onChange={setFilters}
          availableCountries={availableCountries}
          availableRoles={availableRoles}
          usersInView={distinctUsersInView}
          cohortVariant="multi-month"
          showDemoBucketing={false}
          showCohort={false}
        />

        <div style={{ display: "flex", gap: 12, alignItems: "center", margin: "8px 0 16px", flexWrap: "wrap" }}>
          <SegmentToggle<Entity>
            label="Entity"
            value={entity}
            onChange={setEntity}
            options={[
              { key: "user", label: "Users" },
              { key: "company", label: "Companies" },
            ]}
          />
          <label className="filter-segment" style={{ alignItems: "center" }}>
            <span style={{ fontSize: 11, color: "var(--fg-3)", padding: "0 8px" }}>Week:</span>
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              style={{ background: "transparent", border: "none", padding: "4px 8px", fontSize: 13, color: "var(--fg-1)" }}
            >
              {[...weekStarts].reverse().map((w, i) => (
                <option key={w} value={w}>
                  {i === 0 ? `This week (${fmtDate(w)})` : `Week of ${fmtDate(w)}`}
                </option>
              ))}
            </select>
          </label>
        </div>

        {isLoading ? (
          <LoadingPanel />
        ) : isError ? (
          <p style={{ color: "var(--fg-danger)" }}>{error?.message ?? "Query failed."}</p>
        ) : filteredDims.length === 0 ? (
          <EmptyState message="No users match your filter combination. Try widening the filters." />
        ) : (
          <>
            <KpiTiles
              entity={entity}
              current={currentCount}
              previous={priorCount}
              seatPct={seatPct}
              windowLabel={windowLabel}
            />
            <WauTrend
              data={trendData}
              entity={entity}
              pagePills={pagePills}
            />
            <FeatureBreakdown
              rows={featureRows}
              totalActive={currentCount}
              entity={entity}
              pagePills={pagePills}
            />
            <DefinitionsPanel />
          </>
        )}
      </div>
    </>
  );
}

function SegmentToggle<T extends string>({
  label, value, onChange, options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { key: T; label: string }[];
}) {
  return (
    <div role="group" aria-label={label} className="filter-segment">
      <span style={{ fontSize: 11, color: "var(--fg-3)", padding: "0 8px", display: "inline-flex", alignItems: "center" }}>
        {label}:
      </span>
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          className={value === opt.key ? "is-active" : undefined}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
